import React, { useEffect, useRef, useState } from 'react';
import { Box, CssBaseline, ThemeProvider, alpha, createTheme } from '@mui/material';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { DriveSessionRestorer } from './hooks/useDriveSession';
import { SyncCoordinator } from './hooks/useSyncCoordinator';
import { useWarbandStore } from './stores/warbandStore';
import { useGameStore } from './stores/gameStore';
import { useUiPreferencesStore } from './stores/uiPreferencesStore';
import { createIceSurface, liquidGlass, recessedText } from './theme/surfaces';
import { stargraveTheme } from './theme/stargraveTheme';
import { highContrastTheme } from './theme/highContrastTheme';
import './theme/highContrast.css';
import './theme/simple.css';
import { parseUrlState, writeUrlState } from './utils/urlState';
import { AppBar } from './components/common/AppBar';
import { WarbandDrawer } from './components/common/WarbandDrawer';
import { WarbandLayout } from './components/pages/WarbandLayout';
import { LandingPage } from './components/pages/LandingPage';
import { StargraveCrewPage } from './components/pages/StargraveCrewPage';
import { CreateWarbandModal } from './components/modals/CreateWarbandModal';
import { CreateWizardModal } from './components/modals/CreateWizardModal';
import { CreateApprenticeModal } from './components/modals/CreateApprenticeModal';
import { CreateCaptainModal } from './components/modals/CreateCaptainModal';
import { CreateSoldierModal } from './components/modals/CreateSoldierModal';
import { AddSpellModal } from './components/modals/AddSpellModal';
import { SelectStartingSpellsModal } from './components/modals/SelectStartingSpellsModal';
import { ConflictResolutionModal } from './components/modals/ConflictResolutionModal';
import { ImageConflictResolutionModal } from './components/modals/ImageConflictResolutionModal';
import { SyncEntityConflictModal } from './components/modals/SyncEntityConflictModal';
import { CookieConsentModal } from './components/modals/CookieConsentModal';
import { ManageCustomBestiaryModal } from './components/modals/ManageCustomBestiaryModal';
import { ManageCustomEquipmentModal } from './components/modals/ManageCustomEquipmentModal';
import { ManageStargraveBestiaryModal } from './components/modals/ManageStargraveBestiaryModal';
import { ManageStargraveEquipmentModal } from './components/modals/ManageStargraveEquipmentModal';
import { ManageStargraveCustomBuffsModal } from './components/modals/ManageStargraveCustomBuffsModal';
import { ManageCustomBuffsModal } from './components/modals/ManageCustomBuffsModal';
import { getCookieConsent, setCookieConsent as persistCookieConsent } from './services/localStorageService';
import ReactGA from 'react-ga4';
import './App.css';

const gridFlexSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr', md: '1fr', lg: '1fr 1fr', xl: '1fr 1fr 1fr' }, gap: 3};
const miniFlexSx = { display: 'flex', flexWrap: 'wrap', gap: 3};


const editButtonOffsetSx = { position: 'relative', top: -8 };

const treasureEffect = {
  background: `radial-gradient(ellipse farthest-corner at right bottom, #FEDB37 0%, #FDB931 8%, #9f7928 30%, #8A6E2F 40%, transparent 80%), radial-gradient(ellipse farthest-corner at left top, #FFFFFF 0%, #FFFFAC 8%, #D1B464 25%, #5d4a1f 62.5%, #5d4a1f 100%);`,
  boxShadow:
    `inset 0 1px 0 ${alpha('#FFFFFF', 0.6)}, inset 0 -2px 4px ${alpha('#2B1A10', 0.35)}, inset 0 0 0 1px ${alpha('#FFFFFF', 0.16)}, 0 2px 10px ${alpha('#6B3F24', 0.45)}`,
  textShadow: `0px 1px 2px ${alpha('#1a0800', 0.88)}, 0px -1px 0px ${alpha('#FFF5B0', 0.5)}`,
  fontWeight: 700,
  // Simple theme: gradient is stripped, so fall back to a flat gold with dark text
  'body.simple-mode &': { backgroundColor: '#D1B464', color: '#2b1a10' },
};

const treasureHoverEffect = {
  background: `radial-gradient(ellipse farthest-corner at right bottom, #FEDB37 0%, #FDB931 8%, #9f7928 30%, #8A6E2F 40%, transparent 80%), radial-gradient(ellipse farthest-corner at left top, #FFFFFF 0%, #FFFFAC 8%, #D1B464 25%, #5d4a1f 62.5%, #5d4a1f 100%);`,
  boxShadow:
    `inset 0 1px 0 ${alpha('#FFFFFF', 0.54)}, inset 0 -2px 4px ${alpha('#2B1A10', 0.42)}, inset 0 0 0 1px ${alpha('#FFFFFF', 0.12)}, 0 2px 12px ${alpha('#6B3F24', 0.52)}`,
};

const treasureToggleSx = {
  ml: 'auto',
  '&.Mui-selected': {
    color: '#EDD270',
    borderColor: '#6B3F24',
    ...treasureEffect,
  },
  '&.Mui-selected:hover': {
    ...treasureHoverEffect,
  },
};

const frostgraveTheme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    text: {
      primary: '#020617',
      secondary: '#1e293b',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': {
          height: '100%',
        },
        body: {
          backgroundColor: '#6e7883',
          backgroundImage: 'url(/city.jpeg)',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'scroll',
          backgroundSize: 'cover',
          color: recessedText.color,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          // ...createIceSurface(11),
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          // ...createIceSurface(23, { icicles: true, card: true }),
          // transition: 'box-shadow 180ms ease, border-color 180ms ease',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          ...createIceSurface(47, { icicles: true, card: true }),
          transition: 'box-shadow 180ms ease, border-color 180ms ease',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          ...createIceSurface(61),
          filter: 'drop-shadow(0 8px 24px rgba(5, 20, 38, 0.35))',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          ...createIceSurface(78),
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          ...createIceSurface(5, { icicles: true, card: true }),
          borderRadius: 0,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: 'rgba(220, 236, 248, 0.42)',
          fontWeight: 700,
          color: recessedText.color,
          textShadow: recessedText.textShadow,
        },
        body: {
          color: recessedText.color,
          textShadow: recessedText.textShadow,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          ...recessedText,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#1e293b',
          fontWeight: 600,
          textShadow: recessedText.textShadow,
          '&.Mui-selected': {
            color: recessedText.color,
            textShadow: recessedText.textShadow,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: 999,
          backgroundColor: 'rgba(22, 84, 140, 0.9)',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#1e293b',
          textShadow: recessedText.textShadow,
          '&.Mui-focused': {
            color: recessedText.color,
            textShadow: recessedText.textShadow,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: '#334155',
          fontWeight: 500,
          textShadow: recessedText.textShadow,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          color: '#020617',
          fontWeight: 700,
          textShadow: recessedText.textShadow,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        // ── Liquid-glass base applied to ALL contained buttons ──────────────
        // Uses backgroundImage for the diagonal specular shine so it composites
        // naturally below child content — no z-index juggling needed.
        contained: {
          ...liquidGlass({ borderBottomColor: 'rgba(255, 255, 255, 0.14)' }),
          color: '#0f172a',
          textShadow: '0 1px 0 rgba(255, 255, 255, 0.50)',
          transition: 'background-color 200ms ease, box-shadow 200ms ease, background-image 200ms ease',
          '&:hover': {
            backgroundImage:
              'linear-gradient(135deg, rgba(255,255,255,0.54) 0%, rgba(255,255,255,0.12) 52%, rgba(255,255,255,0) 100%)',
          },
          '&:active': {
            backgroundImage:
              'linear-gradient(135deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.04) 52%, rgba(255,255,255,0) 100%)',
            boxShadow: [
              'inset 0 2px 4px rgba(0, 0, 0, 0.14)',
              'inset 0 1px 0 rgba(255, 255, 255, 0.40)',
              '0 1px 4px rgba(0, 0, 0, 0.12)',
            ].join(', '),
          },
          '&.Mui-disabled': {
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
          },
        },
        // ── Blue / primary tint ──────────────────────────────────────────────
        containedPrimary: {
          backgroundColor: 'rgba(48, 113, 163, 0.22)',
          borderColor: 'rgba(100, 160, 215, 0.42)',
          borderBottomColor: 'rgba(48, 113, 163, 0.20)',
          color: '#0c2d4a',
          boxShadow: [
            'inset 0 1px 0 rgba(255, 255, 255, 0.55)',
            'inset 0 0 18px -4px rgba(148, 202, 248, 0.22)',
            '0 4px 16px rgba(22, 78, 127, 0.20)',
          ].join(', '),
          '&:hover': {
            backgroundColor: 'rgba(48, 113, 163, 0.32)',
            boxShadow: [
              'inset 0 1px 0 rgba(255, 255, 255, 0.60)',
              'inset 0 0 20px -4px rgba(148, 202, 248, 0.30)',
              '0 6px 20px rgba(22, 78, 127, 0.28)',
            ].join(', '),
          },
        },
        // ── Red / error tint ─────────────────────────────────────────────────
        containedError: {
          backgroundColor: 'rgba(211, 47, 47, 0.22)',
          borderColor: 'rgba(230, 100, 100, 0.42)',
          borderBottomColor: 'rgba(211, 47, 47, 0.20)',
          color: '#5c1010',
          boxShadow: [
            'inset 0 1px 0 rgba(255, 255, 255, 0.55)',
            'inset 0 0 18px -4px rgba(255, 180, 180, 0.22)',
            '0 4px 16px rgba(140, 20, 20, 0.20)',
          ].join(', '),
          '&:hover': {
            backgroundColor: 'rgba(211, 47, 47, 0.32)',
            boxShadow: [
              'inset 0 1px 0 rgba(255, 255, 255, 0.60)',
              'inset 0 0 20px -4px rgba(255, 180, 180, 0.30)',
              '0 6px 20px rgba(140, 20, 20, 0.28)',
            ].join(', '),
          },
        },
        // ── Orange / warning tint ─────────────────────────────────────────────────
        containedWarning: {
          backgroundColor: 'rgba(255, 152, 0, 0.22)',
          borderColor: 'rgba(255, 183, 77, 0.42)',
          borderBottomColor: 'rgba(255, 152, 0, 0.20)',
          color: '#5c1010',
          boxShadow: [
            'inset 0 1px 0 rgba(255, 255, 255, 0.55)',
            'inset 0 0 18px -4px rgba(255, 180, 180, 0.22)',
            '0 4px 16px rgba(140, 20, 20, 0.20)',
          ].join(', '),
          '&:hover': {
            backgroundColor: 'rgba(255, 152, 0, 0.32)',
            boxShadow: [
              'inset 0 1px 0 rgba(255, 255, 255, 0.60)',
              'inset 0 0 20px -4px rgba(255, 180, 180, 0.30)',
              '0 6px 20px rgba(140, 20, 20, 0.28)',
            ].join(', '),
          },
        },
        // ── Outlined (unchanged — no solid fill, no glass treatment) ─────────
        outlinedPrimary: {
          borderColor: 'rgba(15, 23, 42, 0.35)',
          color: '#020617',
          textShadow: recessedText.textShadow,
          '&:hover': {
            borderColor: 'rgba(15, 23, 42, 0.5)',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          ...liquidGlass({
            bgColor: 'rgba(214, 233, 251, 0.55)',
            gradientPeak: 0.34,
            borderColor: 'rgba(196, 224, 248, 0.55)',
            boxShadowLayers: [
              'inset 0 1px 0 rgba(255, 255, 255, 0.45)',
              '0 2px 8px rgba(7, 24, 41, 0.12)',
            ],
          }),
          // backdrop-filter is too expensive at chip counts — solid fill instead
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          // Wrap long labels on narrow screens instead of truncating
          height: 'auto',
          minHeight: 24,
          '& .MuiChip-label': { whiteSpace: 'normal' },
          color: recessedText.color,
          textShadow: recessedText.textShadow,
          transition: 'background-color 200ms ease, box-shadow 200ms ease, background-image 200ms ease',
          '&:hover': {
            backgroundColor: 'rgba(214, 233, 251, 0.48)',
            backgroundImage:
              'linear-gradient(135deg, rgba(255,255,255,0.46) 0%, rgba(255,255,255,0.12) 52%, rgba(255,255,255,0) 100%)',
            boxShadow: [
              'inset 0 1px 0 rgba(255, 255, 255, 0.55)',
              '0 4px 12px rgba(7, 24, 41, 0.18)',
            ].join(', '),
          },
          '& .MuiChip-deleteIcon': {
            color: 'rgba(14, 52, 93, 0.55)',
            '&:hover': {
              color: 'rgba(14, 52, 93, 0.80)',
            },
          },
        },
        // Selected chips: solid green with black text
        colorPrimary: {
          backgroundColor: '#4caf50',
          backgroundImage: 'none',
          color: '#000000',
          textShadow: 'none',
          border: '1px solid #388e3c',
          '&:hover': {
            backgroundColor: '#43a047',
            backgroundImage: 'none',
          },
          '& .MuiChip-deleteIcon': {
            color: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              color: 'rgba(0, 0, 0, 0.8)',
            },
          },
        },
      },
    },
    // ── FAB: glass treatment matching containedPrimary buttons ──────────────
    MuiFab: {
      styleOverrides: {
        root: {
          ...liquidGlass({
            blur: 16,
            saturate: 180,
            borderBottomColor: 'rgba(255, 255, 255, 0.14)',
          }),
          textShadow: '0 1px 0 rgba(255, 255, 255, 0.50)',
          transition: 'background-color 200ms ease, box-shadow 200ms ease',
          '&:hover': {
            backgroundImage:
              'linear-gradient(135deg, rgba(255,255,255,0.54) 0%, rgba(255,255,255,0.12) 52%, rgba(255,255,255,0) 100%)',
          },
          '&:active': {
            backgroundImage:
              'linear-gradient(135deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.04) 52%, rgba(255,255,255,0) 100%)',
            boxShadow: [
              'inset 0 2px 4px rgba(0, 0, 0, 0.14)',
              'inset 0 1px 0 rgba(255, 255, 255, 0.40)',
              '0 2px 6px rgba(0, 0, 0, 0.14)',
            ].join(', '),
          },
        },
        // ── Blue / primary tint ────────────────────────────────────────────
        primary: {
          backgroundColor: 'rgba(48, 113, 163, 0.52)',
          borderColor: 'rgba(110, 168, 220, 0.65)',
          borderBottomColor: 'rgba(48, 113, 163, 0.45)',
          color: '#0a1e30',
          textShadow: '0 1px 0 rgba(255, 255, 255, 0.25), 0 -1px 1px rgba(7, 40, 70, 0.30)',
          boxShadow: [
            'inset 0 1px 0 rgba(255, 255, 255, 0.52)',
            'inset 0 0 22px -4px rgba(148, 202, 248, 0.28)',
            '0 6px 24px rgba(22, 78, 127, 0.32)',
          ].join(', '),
          '&:hover': {
            backgroundColor: 'rgba(48, 113, 163, 0.65)',
            boxShadow: [
              'inset 0 1px 0 rgba(255, 255, 255, 0.58)',
              'inset 0 0 26px -4px rgba(148, 202, 248, 0.36)',
              '0 8px 28px rgba(22, 78, 127, 0.40)',
            ].join(', '),
          },
        },
      },
    },
    // ── IconButton: frosted glass treatment ──────────────────────────────────
    MuiIconButton: {
      styleOverrides: {
        root: {
          ...liquidGlass({
            saturate: 150,
            bgColor: 'rgba(214, 233, 251, 0.50)',
            gradientPeak: 0.30,
            borderColor: 'rgba(196, 224, 248, 0.55)',
            boxShadowLayers: [
              'inset 0 1px 0 rgba(255, 255, 255, 0.45)',
              '0 2px 8px rgba(7, 24, 41, 0.14)',
            ],
          }),
          // backdrop-filter is too expensive at per-card counts — solid fill instead
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          transition: 'background-color 200ms ease, box-shadow 200ms ease, background-image 200ms ease',
          '&:hover': {
            backgroundColor: 'rgba(214, 233, 251, 0.48)',
            backgroundImage:
              'linear-gradient(135deg, rgba(255,255,255,0.44) 0%, rgba(255,255,255,0.10) 52%, rgba(255,255,255,0) 100%)',
            boxShadow: [
              'inset 0 1px 0 rgba(255, 255, 255, 0.55)',
              '0 4px 12px rgba(7, 24, 41, 0.20)',
            ].join(', '),
          },
          '&:active': {
            backgroundColor: 'rgba(162, 204, 238, 0.40)',
            backgroundImage:
              'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.02) 52%, rgba(255,255,255,0) 100%)',
            boxShadow: [
              'inset 0 2px 4px rgba(7, 24, 41, 0.18)',
              'inset 0 1px 0 rgba(255, 255, 255, 0.35)',
              '0 1px 4px rgba(7, 24, 41, 0.10)',
            ].join(', '),
          },
          '&.Mui-disabled': {
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            backgroundColor: 'transparent',
            border: '1px solid rgba(196, 224, 248, 0.25)',
            boxShadow: 'none',
          },
        },
      },
    },
    // ── ToggleButton: glass on the solid-fill selected state ─────────────────
    // sx-prop overrides (e.g. treasureToggleSx uses `background` shorthand)
    // will naturally take full precedence on bespoke toggle buttons.
    MuiToggleButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            ...liquidGlass({
              blur: 8,
              saturate: 150,
              borderBottomColor: 'rgba(255, 255, 255, 0.14)',
              boxShadowLayers: [
                'inset 0 1px 0 rgba(255, 255, 255, 0.55)',
                'inset 0 0 14px -4px rgba(255, 255, 255, 0.18)',
                '0 3px 10px rgba(0, 0, 0, 0.15)',
              ],
            }),
            color: '#0f172a',
            textShadow: '0 1px 0 rgba(255, 255, 255, 0.50)',
          },
          '&.Mui-selected:hover': {
            backgroundImage:
              'linear-gradient(135deg, rgba(255,255,255,0.54) 0%, rgba(255,255,255,0.12) 52%, rgba(255,255,255,0) 100%)',
          },
        },
      },
    },
  },
});

function App() {
  const {
    selectedWarbandId,
    currentPage,
    manageTabIndex,
    playTabIndex,
    warbands,
    warbandsLoaded,
    selectWarband,
    ui,
  } = useWarbandStore();
  const activeGameSystem = useGameStore((state) => state.activeGameSystem);
  const setActiveGameSystem = useGameStore((state) => state.setActiveGameSystem);

  // Manage mode shows the ship interior; inline style outranks CssBaseline's body styles
  useEffect(() => {
    const useShip = activeGameSystem === 'stargrave' && currentPage === 'manage';
    document.body.style.backgroundImage = useShip
      ? 'linear-gradient(rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.55)), url(/StargraveShip.jpg)'
      : '';
  }, [activeGameSystem, currentPage]);

  const pendingUrlStateRef = useRef(parseUrlState());
  const hydratedFromUrlRef = useRef(false);

  useEffect(() => {
    if (hydratedFromUrlRef.current) {
      return;
    }

    const pendingState = pendingUrlStateRef.current;

    if (!pendingState.warbandId) {
      hydratedFromUrlRef.current = true;
      return;
    }

    if (!warbandsLoaded) {
      return;
    }

    const warbandExists = warbands.some((warband) => warband.id === pendingState.warbandId);

    if (warbandExists) {
      selectWarband(pendingState.warbandId, {
        currentPage: pendingState.currentPage,
        manageTabIndex: pendingState.manageTabIndex,
        playTabIndex: pendingState.playTabIndex,
      });
    }

    hydratedFromUrlRef.current = true;
  }, [warbandsLoaded, warbands, selectWarband]);

  useEffect(() => {
    if (!selectedWarbandId) return;
    const selectedWarband = warbands.find((warband) => warband.id === selectedWarbandId);
    if (selectedWarband?.gameSystem && selectedWarband.gameSystem !== activeGameSystem) {
      setActiveGameSystem(selectedWarband.gameSystem);
    }
  }, [selectedWarbandId, warbands, activeGameSystem, setActiveGameSystem]);

  useEffect(() => {
    if (!hydratedFromUrlRef.current) {
      return;
    }

    writeUrlState({
      warbandId: selectedWarbandId,
      currentPage,
      manageTabIndex,
      playTabIndex,
    });
  }, [selectedWarbandId, currentPage, manageTabIndex, playTabIndex]);

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID ?? '';

  const themeMode = useUiPreferencesStore((state) => state.themeMode);
  const highContrastMode = themeMode === 'highContrast';

  useEffect(() => {
    document.body.classList.toggle('high-contrast-mode', themeMode === 'highContrast');
    document.body.classList.toggle('simple-mode', themeMode === 'simple');
  }, [themeMode]);

  const [cookieConsent, setCookieConsentState] = useState<'accepted' | 'declined' | null>(getCookieConsent);

  const handleCookieConsent = (choice: 'accepted' | 'declined') => {
    persistCookieConsent(choice);
    setCookieConsentState(choice);
  };

  useEffect(() => {
    if (cookieConsent === 'accepted') {
      ReactGA.initialize('G-BGZ9M5SLS0');
      ReactGA.send({ hitType: 'pageview', page: window.location.pathname });
    }
  }, [cookieConsent]);

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <DriveSessionRestorer />
      <SyncCoordinator />
      <ThemeProvider theme={highContrastMode ? highContrastTheme : activeGameSystem === 'stargrave' ? stargraveTheme : frostgraveTheme}>
        <CssBaseline />
        <Box
          className={`game-${activeGameSystem}`}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {/* App Bar */}
          <AppBar />

          {/* Drawer */}
          <WarbandDrawer />

          {/* Main Content Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {selectedWarbandId
              ? activeGameSystem === 'stargrave'
                ? <StargraveCrewPage />
                : <WarbandLayout />
              : <LandingPage />}
          </Box>

          {/* Modals */}
          <CreateWarbandModal />
          <CreateWizardModal />
          <CreateApprenticeModal />
          <CreateCaptainModal />
          <CreateSoldierModal createAsTemporary={ui.createSoldierTemporary} />
          <AddSpellModal fromGrimoire={ui.addSpellModalFromGrimoire} />
          <SelectStartingSpellsModal />
          <ConflictResolutionModal />
          <SyncEntityConflictModal />
          <ImageConflictResolutionModal />
          {activeGameSystem === 'stargrave' ? (
            <>
              <ManageStargraveBestiaryModal />
              <ManageStargraveEquipmentModal />
              <ManageStargraveCustomBuffsModal />
            </>
          ) : (
            <>
              <ManageCustomBestiaryModal />
              <ManageCustomEquipmentModal />
              <ManageCustomBuffsModal />
            </>
          )}
          <CookieConsentModal open={cookieConsent === null} onConsent={handleCookieConsent} />
        </Box>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export { treasureToggleSx, treasureEffect, treasureHoverEffect, gridFlexSx, miniFlexSx, editButtonOffsetSx };
export default App;
